/**
 * Day-spanning bands marking times the SELECTED PATIENT is unavailable.
 *
 * Deliberately a full-width overlay rather than per-column background
 * segments: patient availability is therapist-independent, so a continuous
 * band across all columns reads as the day-scoped fact it is (and costs one
 * DOM node per block instead of blocks × columns). Column-scoped facts keep
 * their column-scoped visuals — non-business dimming for working hours,
 * kind-heat segments for passive treatments.
 *
 * Painted ON TOP of the event blocks (z-index 5, the same layer the grouping
 * bars use), not behind them. Behind is useless in practice: a full schedule
 * hides the band completely, which is exactly when knowing the patient is
 * unavailable matters most. The wash is kept light enough to read every event
 * underneath (measured: ~9.3:1 title contrast under the darkest stripe, still
 * past WCAG AAA), and `pointer-events: none` means clicking, dragging and
 * hovering the covered events behave as if the band were not there.
 *
 * Stacking, verified rather than assumed: `.mc-col` carries `contain: layout`,
 * which makes each column its own stacking context at z-index auto. Event
 * z-indexes (hover 5 … tier-jump 9) are therefore scoped INSIDE their column
 * and cannot rise above this band — every event is washed uniformly, which is
 * the predictable behavior anyway. The interaction overlays are siblings of
 * this band rather than column children, so rings (7–8), the drag ghost (10)
 * and the move hint (11) still paint above it and stay untinted.
 */
import React from "react";
import { MCGridConfig, MCTimeRange } from "../model/types";
import { yForMin } from "../model/gridGeometry";

/** Below this band height the label would overlap its own borders. */
const MIN_LABEL_HEIGHT_PX = 22;

interface PatientBlockedBandProps {
  cfg: MCGridConfig;
  ranges: ReadonlyArray<MCTimeRange>;
}

const PatientBlockedBand: React.FC<PatientBlockedBandProps> = ({ cfg, ranges }) => (
  <>
    {ranges.map((range, i) => {
      const top = yForMin(cfg, range.startMin);
      const height = yForMin(cfg, range.endMin) - top;
      return (
        <div
          key={`${range.startMin}-${range.endMin}-${i}`}
          className="mc-patient-blocked"
          style={{ top, height }}
        >
          {/* A `title` tooltip cannot work on a pointer-transparent element,
              so the band carries its own visible label. */}
          {height >= MIN_LABEL_HEIGHT_PX && (
            <span className="mc-patient-blocked-label">Patient nicht verfügbar</span>
          )}
        </div>
      );
    })}
  </>
);

export default React.memo(PatientBlockedBand);
